import { createFileRoute, Navigate } from '@tanstack/react-router';
import { useTranslation } from 'react-i18next';
import { communityTrpc } from '@org/clients/community';
import type { RichTextDoc } from '@org/model';
import { useCommunityRole } from '@/hooks/use-community-role';
import { SubPageEditor } from '@/components/sub-page-editor';
import { Skeleton } from '@org/ui';

export const Route = createFileRoute('/spaces/$spaceId/pages/$pageId/edit')({
  component: EditSubPage,
});

function EditSubPage() {
  const { spaceId, pageId } = Route.useParams();
  const { t } = useTranslation('community');
  const { isAdmin, isModerator, isLoading: roleLoading } = useCommunityRole();

  const { data: subPage, isLoading: pageLoading } =
    communityTrpc.spaces.subPages.get.useQuery({ subPageId: pageId });

  if (roleLoading || pageLoading) {
    return (
      <div className="space-y-6">
        <Skeleton className="h-8 w-48" />
        <Skeleton className="h-64 w-full rounded-2xl" />
      </div>
    );
  }

  if (!isAdmin && !isModerator(spaceId)) {
    return <Navigate to="/spaces/$spaceId" params={{ spaceId }} />;
  }

  if (!subPage) {
    return (
      <div className="rounded-2xl border border-border bg-card p-8 text-center">
        <p className="text-muted-foreground">{t('subPages.notFound')}</p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <h1 className="text-2xl font-bold">{t('subPages.edit')}</h1>
      <SubPageEditor
        spaceId={spaceId}
        subPageId={pageId}
        initialTitle={subPage.title}
        initialBodyJson={subPage.bodyJson as RichTextDoc | null}
        initialBannerImageUrl={subPage.bannerImageUrl}
        initialBannerVideoUrl={subPage.bannerVideoUrl}
      />
    </div>
  );
}
